import { Injectable } from '@angular/core'
import { NavigationEnd, NavigationStart, Router } from '@angular/router'
import { combineLatest, from, fromEvent, merge, Observable, of, Subject, Subscription, timer } from 'rxjs'
import {
  auditTime,
  distinctUntilChanged,
  filter,
  map,
  share,
  switchMap
} from 'rxjs/operators'
import { subscriptionRegistry } from './subscription-debug'
import { WEBSOCKET_CONFIG } from '../websocket.config'

interface IParkedSubscription {
  release: () => void
}

export interface IVisibilityOptions {
  // used for debugging output
  label?: string
  // for cards in a scrolling list: also count the element as hidden when it has been more than
  // WEBSOCKET_CONFIG.offscreenPixels outside the visible area for WEBSOCKET_CONFIG.offscreenDelay
  viewport?: boolean
}

/*
  Tells components whether their element is actually shown to the user, so they can
  release websocket subscriptions while they are not. Ionic keeps pages alive in the DOM
  (hidden with display: none) when navigating, and this service also treats everything as
  hidden once the app has been in the background for WEBSOCKET_CONFIG.backgroundDelay.
*/
@Injectable({
  providedIn: 'root'
})
export class PageVisibilityService {

  private changed = new Subject<void>()
  private observer: MutationObserver

  // true when the app has been in the background longer than WEBSOCKET_CONFIG.backgroundDelay
  private appHidden = false
  private backgroundTimer: number = null

  // shared scroll events per scroll container
  private scrollEvents = new WeakMap<Element, Observable<Event>>()

  // hidden components whose subscriptions are kept alive (WEBSOCKET_CONFIG.keepAliveLimit)
  private parked = new Set<IParkedSubscription>()

  constructor(
    private router: Router
  ) {
    this.observer = new MutationObserver(() => {
      this.changed.next()
    })

    this.observer.observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: [
        'class',
        'style',
        'hidden'
      ]
    })

    this.router.events.pipe(
      filter(event =>
        event instanceof NavigationStart ||
        event instanceof NavigationEnd
      )
    ).subscribe(() => {
      this.changed.next()
    })

    // app going to the background / coming back (browser tab and capacitor/cordova events)
    merge(
      fromEvent(document, 'visibilitychange'),
      fromEvent(document, 'pause'),
      fromEvent(document, 'resume')
    ).subscribe(() => {
      this.onAppVisibilityChange()
    })

    // release kept alive subscriptions once there are too many
    subscriptionRegistry.changed.pipe(auditTime(0)).subscribe(() => {
      if (subscriptionRegistry.count() > WEBSOCKET_CONFIG.keepAliveLimit) {
        this.releaseParked('more than ' + WEBSOCKET_CONFIG.keepAliveLimit + ' subscriptions')
      }
    })
  }

  // whether subscriptions of a component that just got hidden can stay alive
  private canKeepAlive () {
    return this.isAppVisible() && subscriptionRegistry.count() <= WEBSOCKET_CONFIG.keepAliveLimit
  }

  private releaseParked (reason: string) {
    if (this.parked.size == 0) return
    subscriptionRegistry.debug('releasing ' + this.parked.size + ' kept alive subscriptions: ' + reason)
    const parked = Array.from(this.parked)
    this.parked.clear()
    parked.forEach(entry => entry.release())
  }

  private onAppVisibilityChange () {
    if (document.visibilityState == 'hidden') {
      if (this.backgroundTimer == null && !this.appHidden) {
        this.backgroundTimer = window.setTimeout(() => {
          this.backgroundTimer = null
          // timers can be paused until the app is resumed, in that case nothing should be released
          if (document.visibilityState != 'hidden') return
          this.appHidden = true
          subscriptionRegistry.debug('app in background for ' + (WEBSOCKET_CONFIG.backgroundDelay / 1000) + 's, releasing all subscriptions')
          this.releaseParked('app in background')
          this.changed.next()
        }, WEBSOCKET_CONFIG.backgroundDelay)
      }
    } else {
      if (this.backgroundTimer != null) {
        window.clearTimeout(this.backgroundTimer)
        this.backgroundTimer = null
      }
      if (this.appHidden) {
        this.appHidden = false
        subscriptionRegistry.debug('app resumed, subscribing again')
        this.changed.next()
      }
    }
  }

  isAppVisible () {
    return !this.appHidden
  }

  observe(element: HTMLElement): Observable<boolean> {
    if (!WEBSOCKET_CONFIG.enabled) {
      return of(true)
    }

    return merge(
      of(null),
      this.changed
    ).pipe(
      auditTime(0),

      map(() => {
        return this.isVisible(element)
      }),

      distinctUntilChanged()
    )
  }

  // like observe(), but with opt.viewport the element also counts as hidden after it has been
  // far off-screen for a while (see WEBSOCKET_CONFIG)
  observeVisible (element: HTMLElement, opt: IVisibilityOptions = {}): Observable<boolean> {
    if (!opt.viewport || !WEBSOCKET_CONFIG.enabled) {
      return this.observe(element)
    }

    const near$ = this.observeNearViewport(element).pipe(
      switchMap(near => {
        if (near) return of(true)
        return timer(WEBSOCKET_CONFIG.offscreenDelay).pipe(map(() => false))
      }),
      distinctUntilChanged()
    )

    return combineLatest([this.observe(element), near$]).pipe(
      map(([visible, near]) => visible && near),
      distinctUntilChanged()
    )
  }

  private isVisible(element: HTMLElement): boolean {
    if (!this.isAppVisible()) {
      return false
    }

    if (!element.isConnected) {
      return false
    }

    if (typeof element['checkVisibility'] == 'function') {
      return element['checkVisibility']({checkOpacity: true, checkVisibilityCSS: true})
    }

    // empty when the element or one of its parents has display: none
    if (element.getClientRects().length == 0) {
      return false
    }

    let parent: HTMLElement = element

    while (parent) {
      const style = getComputedStyle(parent)

      if (
        style.display == 'none' ||
        style.visibility == 'hidden' ||
        style.visibility == 'collapse' ||
        style.opacity == '0'
      ) {
        return false
      }

      parent = parent.parentElement
    }

    return true
  }

  // the ion-content the element scrolls in, or null when it is not inside one
  private scrollContainer (element: HTMLElement): HTMLElement | null {
    return element.closest('ion-content') as HTMLElement
  }

  // scroll events of a scroll container. ion-content scrolls an element inside its shadow dom,
  // those scroll events never reach the document, so the listener is put directly on it
  private observeScroll (container: HTMLElement | null): Observable<Event> {
    if (!container) {
      return fromEvent(window, 'scroll', {passive: true})
    }

    let events = this.scrollEvents.get(container)

    if (!events) {
      const scrollElement: Promise<HTMLElement> = typeof container['getScrollElement'] == 'function'
        ? container['getScrollElement']()
        : Promise.resolve(container)

      events = from(scrollElement).pipe(
        switchMap(el => fromEvent(el, 'scroll', {passive: true})),
        share()
      )
      this.scrollEvents.set(container, events)
    }

    return events
  }

  private observeNearViewport (element: HTMLElement): Observable<boolean> {
    const container = this.scrollContainer(element)

    // the first value right away (cards should not wait to subscribe), later changes throttled
    return merge(
      of(null),
      merge(this.observeScroll(container), this.changed).pipe(auditTime(100))
    ).pipe(
      map(() => this.isNearViewport(element, container)),
      distinctUntilChanged()
    )
  }

  // whether the element is within WEBSOCKET_CONFIG.offscreenPixels of the visible area of its
  // scroll container (the window when there is none)
  private isNearViewport (element: HTMLElement, container: HTMLElement | null): boolean {
    const rect = element.getBoundingClientRect()
    const area = container
      ? container.getBoundingClientRect()
      : new DOMRect(0, 0, window.innerWidth, window.innerHeight)

    // distance outside the visible area, negative or zero when (partly) inside it
    const distance = Math.max(
      area.top - rect.bottom,
      rect.top - area.bottom,
      area.left - rect.right,
      rect.left - area.right
    )

    return distance <= WEBSOCKET_CONFIG.offscreenPixels
  }

  // runs subscribe() while the element is visible, and unsubscribes the result when it is hidden
  whileVisible(
    element: HTMLElement,
    subscribe: () => Subscription,
    opt: IVisibilityOptions = {}
  ): Subscription {
    let activeSubscription: Subscription = null

    const release = () => {
      activeSubscription?.unsubscribe()
      activeSubscription = null
    }
    const entry: IParkedSubscription = {release}

    const visibilitySubscription = this.observeVisible(element, opt).subscribe(visible => {
      if (visible) {
        this.parked.delete(entry)
        if (!activeSubscription || activeSubscription.closed) {
          if (opt.label) subscriptionRegistry.debug('visible: ' + opt.label)
          activeSubscription = subscribe()
        }
      } else if (activeSubscription && this.canKeepAlive()) {
        // hidden, but there are few enough subscriptions to keep it alive
        if (opt.label) subscriptionRegistry.debug('hidden, kept alive: ' + opt.label)
        this.parked.add(entry)
      } else {
        if (activeSubscription && opt.label) subscriptionRegistry.debug('hidden: ' + opt.label)
        release()
      }
    })

    const result = new Subscription()

    result.add(visibilitySubscription)

    result.add(() => {
      this.parked.delete(entry)
      release()
    })

    return result
  }
}
